const AUTH_ISSUER = "https://auth.g1.network";
const CLIENT_ID = "markets-portal-spa";
const REDIRECT_URI = `${location.origin}/auth/callback`;
const SCOPES = "openid email profile markets";

const ACCESS_KEY = "markets.access_token";
const REFRESH_KEY = "markets.refresh_token";
const VERIFIER_KEY = "markets.code_verifier";
const STATE_KEY = "markets.oauth_state";
const RETURN_KEY = "markets.return_to";

export function getAccessToken(): string | null {
	return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
	return localStorage.getItem(REFRESH_KEY);
}

export function clearTokens(): void {
	localStorage.removeItem(ACCESS_KEY);
	localStorage.removeItem(REFRESH_KEY);
}

export async function startLogin(returnTo: string = location.pathname): Promise<void> {
	const verifier = randomBase64Url(32);
	const challenge = await sha256base64url(verifier);
	const state = randomBase64Url(16);

	localStorage.setItem(VERIFIER_KEY, verifier);
	localStorage.setItem(STATE_KEY, state);
	localStorage.setItem(RETURN_KEY, returnTo);

	const url = new URL(`${AUTH_ISSUER}/oauth/authorize`);
	url.searchParams.set("response_type", "code");
	url.searchParams.set("client_id", CLIENT_ID);
	url.searchParams.set("redirect_uri", REDIRECT_URI);
	url.searchParams.set("scope", SCOPES);
	url.searchParams.set("state", state);
	url.searchParams.set("code_challenge", challenge);
	url.searchParams.set("code_challenge_method", "S256");
	location.href = url.toString();
}

export async function handleCallback(): Promise<
	{ ok: true; returnTo: string } | { ok: false; error: string }
> {
	const params = new URLSearchParams(location.search);
	const code = params.get("code");
	const state = params.get("state");
	const err = params.get("error");
	if (err) return { ok: false, error: `oauth_error:${err}` };
	if (!code || !state) return { ok: false, error: "missing_code_or_state" };

	const expected = localStorage.getItem(STATE_KEY);
	if (state !== expected) return { ok: false, error: "state_mismatch" };
	const verifier = localStorage.getItem(VERIFIER_KEY);
	if (!verifier) return { ok: false, error: "missing_verifier" };

	const body = new URLSearchParams({
		grant_type: "authorization_code",
		code,
		client_id: CLIENT_ID,
		redirect_uri: REDIRECT_URI,
		code_verifier: verifier,
	});
	const res = await fetch(`${AUTH_ISSUER}/oauth/token`, {
		method: "POST",
		headers: { "content-type": "application/x-www-form-urlencoded" },
		body,
	});
	if (!res.ok) {
		const text = await res.text();
		return { ok: false, error: `token_exchange_failed:${res.status}:${text.slice(0, 200)}` };
	}
	const json = (await res.json()) as {
		access_token: string;
		refresh_token?: string;
	};
	localStorage.setItem(ACCESS_KEY, json.access_token);
	if (json.refresh_token) localStorage.setItem(REFRESH_KEY, json.refresh_token);
	const returnTo = localStorage.getItem(RETURN_KEY) ?? "/";
	localStorage.removeItem(VERIFIER_KEY);
	localStorage.removeItem(STATE_KEY);
	localStorage.removeItem(RETURN_KEY);
	return { ok: true, returnTo };
}

export async function refreshAccessToken(): Promise<boolean> {
	const refresh = getRefreshToken();
	if (!refresh) return false;
	const body = new URLSearchParams({
		grant_type: "refresh_token",
		refresh_token: refresh,
		client_id: CLIENT_ID,
	});
	const res = await fetch(`${AUTH_ISSUER}/oauth/token`, {
		method: "POST",
		headers: { "content-type": "application/x-www-form-urlencoded" },
		body,
	});
	if (!res.ok) {
		clearTokens();
		return false;
	}
	const json = (await res.json()) as { access_token: string; refresh_token?: string };
	localStorage.setItem(ACCESS_KEY, json.access_token);
	if (json.refresh_token) localStorage.setItem(REFRESH_KEY, json.refresh_token);
	return true;
}

export function decodeJwtClaims(): { sub?: string; email?: string; app_access?: string[] } | null {
	const token = getAccessToken();
	if (!token) return null;
	const parts = token.split(".");
	if (parts.length !== 3) return null;
	try {
		const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
		const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
		return JSON.parse(atob(padded));
	} catch {
		return null;
	}
}

function randomBase64Url(byteLen: number): string {
	const bytes = new Uint8Array(byteLen);
	crypto.getRandomValues(bytes);
	let bin = "";
	for (const b of bytes) bin += String.fromCharCode(b);
	return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function sha256base64url(s: string): Promise<string> {
	const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
	const bytes = new Uint8Array(buf);
	let bin = "";
	for (const b of bytes) bin += String.fromCharCode(b);
	return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
